import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import Badge, { type BadgeTone } from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import { formatDateTime } from '../../../lib/datetime'
import type { SentEmailListItem } from '../types'

interface ColumnOpts {
  t: TFunction
  onDetail: (row: SentEmailListItem) => void
  /**
   * Deneme sayısını sıfırla (078-b). `undefined` → kullanıcının `SENT_EMAIL_MANAGE` yetkisi yoktur ve
   * buton **hiç render edilmez** (disabled değil): yetkisiz kullanıcıya, basamayacağı bir aksiyonun
   * varlığını göstermenin bir faydası yok. Yetki varken de buton yalnız `row.retryResettable` olan
   * satırlarda çıkar (web kanalı hariç — bkz. `SentEmailListItem.retryResettable`).
   */
  onResetRetry?: (row: SentEmailListItem) => void
  /** Sıfırlama isteği süren satır — yalnız o satırın butonu kilitlenir (059 `busyId` deseni). */
  busyId?: number | null
}

/**
 * Gönderim durumu → rozet tonu. Kimlikler `REFERANS` (TYPE=SEND_STATUS) kataloğundandır ve **kapalı
 * bir kümedir** (DB CHECK kısıtı: 20/21/22/29 — canlıda doğrulandı, research 078 Adım 0):
 * 21 Gönderildi → yeşil · 29 İptal Edildi → kırmızı · 22 Gönderilmeyecek → nötr ·
 * 20 Gönderilecek (ve bilinmeyen) → mavi.
 *
 * <p>Etiket **metni** koda gömülmez — sunucudan dile göre çözülmüş `sendStatusName` gelir; burada
 * yalnız <b>renk</b> eşlenir.
 */
export function statusTone(sendStatusId: number): BadgeTone {
  switch (sendStatusId) {
    case 21:
      return 'green'
    case 29:
      return 'red'
    case 22:
      return 'neutral'
    default:
      return 'blue'
  }
}

/**
 * Admin → Bildirimler tablosu sütunları (078; üç kanal birden).
 *
 * <p>Bilinçli olarak **yok olanlar**: hiçbir sütun `sortable` değildir (düzen sunucuda sabit — spec
 * FR-004; istemci sıralaması yalnız görünen sayfayı sıralar ve kullanıcıyı yanıltırdı) ve satır
 * aksiyonlarında ekle/düzenle/sil **yoktur** (spec FR-001). İki aksiyon vardır: **Detay** (herkese) ve
 * **Deneme Sayısını Sıfırla** (078-b; yalnız `SENT_EMAIL_MANAGE` yetkisi olana).
 *
 * <p><b>"Bildirim Tipi" sütunu düz metindir, rozet DEĞİLDİR</b> (kullanıcı isteği, 2026-08-07):
 * hemen yanındaki "Gönderim Durumu" zaten renkli bir rozettir ve aynı paletten ikinci bir rozet
 * (`BadgeTone` yalnız dört ton sunar) hangi rengin neyi anlattığını bulanıklaştırırdı. Bu yüzden
 * satır ham `notificationTypeId` **taşımaz** — gösterilen tek şey sunucuda dile göre çözülmüş addır.
 *
 * <p><b>"İşlem Kodu" SÜTUNU yoktur — ama filtresi vardır.</b> Kolon (`SEND_ITEM.OPERATION_CODE_ID`)
 * 078 teslim edildikten sonra şemaya eklendi ve research D1'in "kolon yok → filtre ve sütun düşürüldü"
 * kararı **yalnız filtre için** geri alındı (kullanıcı isteği, 2026-08-06). Sütun eklenmedi çünkü tablo
 * zaten yedi sütunludur ve seçili filtre yüzünden tüm satırlar aynı kodu taşır → sütun her satırda aynı
 * metni yinelerdi. Değer **detay modalında** görünür (`SentEmailDetailDialog`). Bildirim tipinde bu
 * gerekçe **geçerli değildir**: filtre varsayılan olarak "Tümü"dür, dolayısıyla sütun gerçekten
 * ayırt edici bilgi taşır.
 *
 * <p>Boş hücrelere yer tutucu (`-`/`—`) **yazılmaz** (spec FR-010): gönderilmemiş bir kaydın
 * "Gönderim Zamanı" hücresi boş kalır, böylece dolu/boş ayrımı göz taramasıyla görülür.
 *
 * <p><b>"Gönderilen Sicil" sütunu (100).</b> Sekizinci veri sütunu olarak eklendi; "İşlem Kodu"
 * için geçerli olan "tablo zaten kalabalık" gerekçesi burada **geçmez**: o sütun seçili filtre
 * yüzünden her satırda aynı metni yinelerdi, bu sütun ise satırdan satıra değişen ve **kararın
 * kendisini** gösteren bilgiyi taşır (bildirim gerçekte hangi sicile gitti?). Değeri yalnız push
 * satırlarında dolar.
 */
export function sentEmailColumns({
  t,
  onDetail,
  onResetRetry,
  busyId,
}: ColumnOpts): ReactElement[] {
  return [
    <Column
      key="contactInformation"
      field="contactInformation"
      header={t('admin.sentEmails.columns.recipient')}
      style={{ width: '16rem' }}
      body={(row: SentEmailListItem) => (
        <span className="admin-cell-ellipsis" title={row.contactInformation}>
          {row.contactInformation}
        </span>
      )}
    />,
    // Gerçek gönderim sicili (100). "Alıcı"nın HEMEN YANINDA durur: iki sütunun anlamı ancak yan
    // yana okununca çıkıyor — kaydın taşıdığı sicil ile bildirimin fiilen gittiği sicil. Çoklu
    // sicilli personelde bu ikisi farklıdır ve fark tam olarak bu işin sebebidir.
    // Boş hücreye yer tutucu YAZILMAZ (spec FR-010/FR-017): eposta/web satırlarında ve henüz
    // gönderilmemiş push kayıtlarında değer yoktur; boşluk "bu satırda sicil çözümlemesi
    // yapılmadı" bilgisini göz taramasıyla verir.
    <Column
      key="sendContactInformation"
      field="sendContactInformation"
      header={t('admin.sentEmails.columns.sentRecipient')}
      style={{ width: '11rem' }}
      body={(row: SentEmailListItem) => (
        <span className="admin-cell-ellipsis" title={row.sendContactInformation ?? ''}>
          {row.sendContactInformation ?? ''}
        </span>
      )}
    />,
    <Column
      key="notificationType"
      field="notificationTypeName"
      header={t('admin.sentEmails.columns.notificationType')}
      style={{ width: '9rem' }}
      body={(row: SentEmailListItem) => row.notificationTypeName ?? ''}
    />,
    <Column
      key="subject"
      field="subject"
      header={t('admin.sentEmails.columns.subject')}
      body={(row: SentEmailListItem) => (
        <span className="admin-cell-ellipsis" title={row.subject ?? ''}>
          {row.subject ?? ''}
        </span>
      )}
    />,
    <Column
      key="status"
      header={t('admin.sentEmails.columns.status')}
      style={{ width: '12rem' }}
      body={(row: SentEmailListItem) => (
        <span className="admin-status-cell">
          <Badge tone={statusTone(row.sendStatusId)}>{row.sendStatusName ?? ''}</Badge>
          {/*
            Hata varsa uyarı ikonu + tooltip (spec FR-008). Tam metin detaydadır; `title` ile
            gösterilir çünkü hücre dar ve metin 4000 karaktere kadar çıkabilir.
          */}
          {row.errorMessage && (
            <i
              className="pi pi-exclamation-triangle admin-error-flag"
              title={row.errorMessage}
              aria-label={t('admin.sentEmails.columns.hasError')}
            />
          )}
        </span>
      )}
    />,
    <Column
      key="plannedSendingTime"
      header={t('admin.sentEmails.columns.planned')}
      style={{ width: '11rem' }}
      body={(row: SentEmailListItem) =>
        row.plannedSendingTime ? formatDateTime(row.plannedSendingTime) : ''
      }
    />,
    <Column
      key="sendingTime"
      header={t('admin.sentEmails.columns.sending')}
      style={{ width: '11rem' }}
      body={(row: SentEmailListItem) => (row.sendingTime ? formatDateTime(row.sendingTime) : '')}
    />,
    <Column
      key="retryCount"
      field="retryCount"
      header={t('admin.sentEmails.columns.retryCount')}
      style={{ width: '8rem' }}
      align="center"
    />,
    <Column
      key="actions"
      header={t('admin.sentEmails.columns.actions')}
      style={{ width: onResetRetry ? '8rem' : '6rem' }}
      body={(row: SentEmailListItem) => (
        <div className="admin-row-actions">
          <Button
            icon="pi pi-eye"
            rounded
            text
            aria-label={t('admin.sentEmails.actions.detail')}
            title={t('admin.sentEmails.actions.detail')}
            onClick={() => onDetail(row)}
          />
          {/*
            Sıfırlama ikonu `pi-replay`: eylem kaydı "başa sarar" (sayaç 0 + yeniden kuyruğa). `pi-refresh`
            KULLANILMAZ — o, filtre çubuğundaki "Yenile" butonunun ikonudur ve aynı ekranda iki farklı işi
            aynı simgeye bağlamak yanıltıcı olurdu.
          */}
          {/*
            `row.retryResettable`: kararı SUNUCU verir (web satırlarında `false`) — satır ham kanal
            kimliği taşımadığı için istemci bu kuralı yeniden kuramaz. Buton disabled değil, hiç
            RENDER EDİLMEZ: web satırında eylem yanlış olurdu, "geçici olarak kapalı" değil.
          */}
          {onResetRetry && row.retryResettable && (
            <Button
              icon="pi pi-replay"
              rounded
              text
              // Yıkıcı eylem — onay penceresindeki `p-button-danger` ile aynı dil (059 sil butonu deseni).
              severity="danger"
              disabled={busyId === row.id}
              aria-label={t('admin.sentEmails.actions.resetRetry')}
              title={t('admin.sentEmails.actions.resetRetry')}
              onClick={() => onResetRetry(row)}
            />
          )}
        </div>
      )}
    />,
  ]
}
